import React, { useState, useEffect } from 'react';
import { homeApi } from '../../api/api';
import { AdminFieldInput, AdminFieldTextarea } from '../components/AdminUI';

const PHONE_FIELDS = [
  { key: 'ashramOffice', label: 'Ashram Office', placeholder: '+91 98765 43210' },
  { key: 'eventsInfo', label: 'Events Info', placeholder: '+91 98765 43211' },
  { key: 'sevaCoordination', label: 'Seva Coordination', placeholder: '+91 98765 43212' },
  { key: 'emergency', label: 'Emergency', placeholder: '+91 98765 43213' },
];

const EMAIL_FIELDS = [
  { key: 'general', label: 'General', placeholder: 'info@shreeramranujadham.org' },
  { key: 'events', label: 'Events', placeholder: 'events@shreeramranujadham.org' },
  { key: 'seva', label: 'Seva', placeholder: 'seva@shreeramranujadham.org' },
  { key: 'guidance', label: 'Guidance', placeholder: 'spiritual@shreeramranujadham.org' },
];

const CARDS = [
  {
    key: 'ashram_phones',
    id: 'phones',
    title: 'Divine Contact',
    icon: 'fas fa-phone',
    iconColor: 'text-green-600',
    hint: 'Phone numbers on the Contact page — one field per contact.',
    type: 'phones',
    fields: PHONE_FIELDS,
    defaults: {
      ashramOffice: '',
      eventsInfo: '',
      sevaCoordination: '',
      emergency: '',
    },
  },
  {
    key: 'ashram_emails',
    id: 'emails',
    title: 'Sacred Communication',
    icon: 'fas fa-envelope',
    iconColor: 'text-purple-600',
    hint: 'Email addresses on the Contact page — one field per email.',
    type: 'emails',
    fields: EMAIL_FIELDS,
    defaults: {
      general: '',
      events: '',
      seva: '',
      guidance: '',
    },
  },
  {
    key: 'ashram_address',
    id: 'address',
    title: 'Ashram Address',
    icon: 'fas fa-map-marker-alt',
    iconColor: 'text-orange-500',
    hint: 'Shown on the Contact page address card. One line per row.',
    type: 'address',
    defaults: {
      body: '',
    },
  },
];

function parseLabeledBody(body, fields) {
  const out = {};
  fields.forEach((f) => { out[f.key] = ''; });
  if (!body) return out;
  String(body).replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    const m = line.match(/^([^:]+):\s*(.*)$/);
    if (!m) return;
    const label = m[1].trim().toLowerCase();
    const value = m[2].trim();
    const match = fields.find((f) => f.label.toLowerCase() === label);
    if (match) out[match.key] = value;
  });
  return out;
}

function normalizeCard(raw, card) {
  const d = raw && typeof raw === 'object' ? raw : {};

  if (card.type === 'address') {
    return {
      body: d.body != null ? String(d.body) : '',
    };
  }

  const fromBody = d.body != null && String(d.body).trim()
    ? parseLabeledBody(d.body, card.fields)
    : null;

  const next = {};
  card.fields.forEach((f) => {
    const direct = d[f.key] != null ? String(d[f.key]).trim() : '';
    next[f.key] = direct || (fromBody && fromBody[f.key]) || '';
  });
  return next;
}

function payloadForCard(card, form) {
  if (card.type === 'address') {
    return {
      body: (form.body || '').replace(/\r\n/g, '\n').trim(),
    };
  }
  const payload = {};
  card.fields.forEach((f) => {
    payload[f.key] = (form[f.key] || '').trim();
  });
  return payload;
}

const AshramDetailsAdmin = () => {
  const [forms, setForms] = useState(() =>
    Object.fromEntries(CARDS.map((c) => [c.id, { ...c.defaults }]))
  );
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    setLoading(true);
    homeApi
      .getAll()
      .then((res) => {
        if (res.success && res.data) {
          const d = res.data;
          setForms(() => {
            const next = {};
            CARDS.forEach((c) => {
              next[c.id] = normalizeCard(d[c.key], c);
            });
            return next;
          });
        } else if (res.message) {
          setError(res.message);
        }
      })
      .catch(() => setError('Failed to load ashram details'))
      .finally(() => setLoading(false));
  }, []);

  const setField = (id, field, value) => {
    setForms((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const saveCard = async (card) => {
    const form = forms[card.id] || card.defaults;
    setSaving((prev) => ({ ...prev, [card.id]: true }));
    setError('');
    try {
      await homeApi.updateKey(card.key, payloadForCard(card, form));
      showToast(`${card.title} saved`);
    } catch {
      setError(`Failed to save ${card.title}`);
    } finally {
      setSaving((prev) => ({ ...prev, [card.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <i className="fas fa-spinner fa-spin text-orange-500 text-2xl"></i>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Ashram Details</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Address, phone, and email cards on the Contact page
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {CARDS.map((card) => {
          const form = forms[card.id] || card.defaults;
          const isSaving = !!saving[card.id];
          return (
            <div
              key={card.id}
              className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6 flex flex-col${
                card.id === 'address' ? ' md:col-span-2' : ''
              }`}
            >
              <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                <i className={`${card.icon} ${card.iconColor}`}></i>
                {card.title}
              </h3>
              <p className="text-sm text-gray-500 mb-5">{card.hint}</p>

              <div className="space-y-4 flex-1">
                {card.type === 'address' ? (
                  <AdminFieldTextarea
                    label="Address"
                    rows={5}
                    value={form.body}
                    onChange={(e) => setField(card.id, 'body', e.target.value)}
                    placeholder={card.defaults.body}
                  />
                ) : (
                  card.fields.map((f) => (
                    <AdminFieldInput
                      key={f.key}
                      label={f.label}
                      value={form[f.key] || ''}
                      onChange={(e) => setField(card.id, f.key, e.target.value)}
                      onClear={() => setField(card.id, f.key, '')}
                      clearable
                      placeholder={f.placeholder}
                    />
                  ))
                )}
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => saveCard(card)}
                  disabled={isSaving}
                  className="admin-home-save-btn disabled:opacity-60"
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AshramDetailsAdmin;
